/**
 * LivingLogoLED - Standalone LED micro-dot component
 * 
 * Extracted from LivingLogoIcon to allow independent positioning.
 * Uses the same LED position, size, and heartbeat animation as the original.
 * 
 * @see src/components/brand/LivingLogoIcon.tsx - Original LED implementation
 */

import { cn } from '@/lib/utils';
import React from 'react';

// Brand colors from Brand Book SSOT
const BRAND_COLORS = {
  ledRed: '#FF2A00',
} as const;

// LED position relative to 256×256 viewBox (same as LivingLogoIcon)
// Position: bottom right corner, near end of glyph
const LED_POSITION = { cx: 210, cy: 220 };

// LED radius (micro size)
const LED_RADIUS = 4;

export interface LivingLogoLEDProps {
  /** Size of the parent icon/logo in pixels (used for positioning calculation) */
  logoSize?: number;
  /** Additional className for positioning */
  className?: string;
  /** Whether to show the LED (default: true) */
  show?: boolean;
}

/**
 * Standalone LED micro-dot component
 * 
 * Can be positioned independently outside the logo/button contour.
 * Uses the same heartbeat animation as LivingLogoIcon's built-in LED.
 */
export const LivingLogoLED: React.FC<LivingLogoLEDProps> = ({
  logoSize = 42,
  className,
  show = true,
}) => {
  if (!show) return null;

  // Calculate LED position relative to button center
  // In 256×256 viewBox, center is at (128, 128)
  // LED is at (210, 220), so offset from center:
  const centerX = 128;
  const centerY = 128;
  const offsetX = (LED_POSITION.cx - centerX) / 256; // ~0.32 (right)
  const offsetY = (LED_POSITION.cy - centerY) / 256; // ~0.36 (down)

  // Scale to logo size to get position relative to logo center
  const scaledOffsetX = offsetX * logoSize;
  const scaledOffsetY = offsetY * logoSize;

  // Position LED OUTSIDE button contour (button is 64px, logo is 36px centered)
  // LED should be at the edge of button + a bit outside
  const buttonSize = 64; // h-16 w-16
  const buttonRadius = buttonSize / 2; // 32px
  // Position LED at button edge + small offset outside
  const outsideOffset = 2; // 2px outside button edge

  // Generate unique IDs for filters (to avoid conflicts)
  const ledGlowFilterId = `led-glow-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <svg
      viewBox="0 0 256 256"
      width={LED_RADIUS * 2 + 8} // Extra space for glow
      height={LED_RADIUS * 2 + 8}
      className={cn('absolute pointer-events-none', className)}
      style={{
        // Position relative to button center, but OUTSIDE the button contour
        // Start from logo position, then extend to button edge + outside
        left: `calc(50% + ${scaledOffsetX}px + ${buttonRadius - logoSize / 2 + outsideOffset}px - ${LED_RADIUS + 4}px)`,
        top: `calc(50% + ${scaledOffsetY}px + ${buttonRadius - logoSize / 2 + outsideOffset}px - ${LED_RADIUS + 4}px)`,
      }}
      aria-hidden="true"
    >
      <defs>
        {/* LED Glow Filter - Enhanced for dark backgrounds */}
        <filter id={ledGlowFilterId} x="-200%" y="-200%" width="500%" height="500%">
          <feGaussianBlur stdDeviation="3" result="blur" />
          <feColorMatrix
            in="blur"
            type="matrix"
            values="1 0 0 0 0  0 0.1 0 0 0  0 0 0 0 0  0 0 0 1 0"
            result="coloredBlur"
          />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* LED Heartbeat - micro dot, blinks every 5s */}
      {/* Enhanced visibility on dark backgrounds with stronger glow */}
      <g className="animate-led-heartbeat">
        <circle
          cx={128 + (LED_POSITION.cx - centerX)}
          cy={128 + (LED_POSITION.cy - centerY)}
          r={LED_RADIUS}
          fill={BRAND_COLORS.ledRed}
          filter={`url(#${ledGlowFilterId})`}
          className="drop-shadow-[0_0_4px_rgba(255,42,0,0.8)]"
        />
      </g>

      {/* Inline styles for LED heartbeat animation */}
      <style>{`
        @keyframes led-heartbeat {
          0%, 89% { opacity: 0; transform: scale(0.8); }
          90% { opacity: 1; transform: scale(1.3); }
          92% { opacity: 0.4; transform: scale(1); }
          94% { opacity: 1; transform: scale(1.2); }
          96% { opacity: 0.6; transform: scale(1); }
          100% { opacity: 0; transform: scale(0.8); }
        }
        .animate-led-heartbeat {
          animation: led-heartbeat 5s ease-in-out infinite;
          transform-origin: ${128 + (LED_POSITION.cx - centerX)}px ${128 + (LED_POSITION.cy - centerY)}px;
        }
        
        @media (prefers-reduced-motion: reduce) {
          .animate-led-heartbeat {
            animation: none;
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
    </svg>
  );
};
